import React, { useState } from 'react';
import { Medication, MedicationType } from '../../types/api';
import { MEDICATION_UNIT_OPTIONS } from '../../constants/medicationUnits';

export interface MedicationSectionProps {
  medications: Medication[];
  setMedications: (medications: Medication[]) => void;
  medicationTypes: MedicationType[];
  medicationsEnabled?: boolean;
  setMedicationsEnabled?: (enabled: boolean) => void;
  onMedicationsChange?: () => void;
  showToggle?: boolean;
  clinicId?: number;
}

export const MedicationSection: React.FC<MedicationSectionProps> = ({
  medications,
  setMedications,
  medicationTypes,
  medicationsEnabled = true,
  setMedicationsEnabled,
  onMedicationsChange,
  showToggle = true,
  clinicId,
}) => {
  // Form state
  const [medicationForm, setMedicationForm] = useState({
    medication: '',
    medication_type_id: 0,
    dosage: '',      // Will be decimal string
    unit: '',        // Will be selected from dropdown
    amount: '',      // Will be decimal string
    amount_per: 'Day' as 'Day' | 'Week' | 'Month',
    duration: '',    // Will be integer string
    comments: '',
  });
  const [medicationError, setMedicationError] = useState('');
  const [medicationSearch, setMedicationSearch] = useState('');
  const [showMedicationDropdown, setShowMedicationDropdown] = useState(false);

  // Validation
  const validateMedicationForm = (): boolean => {
    // Validate dosage (decimal)
    const dosageNum = parseFloat(medicationForm.dosage);
    const dosageValid =
      !isNaN(dosageNum) && medicationForm.dosage !== '' && dosageNum >= 0;

    // Validate amount (decimal)
    const amountNum = parseFloat(medicationForm.amount);
    const amountValid =
      !isNaN(amountNum) && medicationForm.amount !== '' && amountNum >= 0;

    // Validate duration (integer only)
    const durationNum = parseInt(medicationForm.duration, 10);
    const durationValid =
      !isNaN(durationNum) &&
      medicationForm.duration !== '' &&
      durationNum > 0 &&
      Number.isInteger(durationNum) &&
      medicationForm.duration === durationNum.toString(); // Ensures no decimals

    // Validate unit is selected
    const unitValid = medicationForm.unit !== '';

    if (!dosageValid) {
      setMedicationError('Dosage must be a valid decimal number (e.g., 1.5)');
      return false;
    }

    if (!amountValid) {
      setMedicationError('Amount must be a valid decimal number (e.g., 2.0)');
      return false;
    }

    if (!durationValid) {
      setMedicationError('Duration must be a valid integer (e.g., 30)');
      return false;
    }

    if (!unitValid) {
      setMedicationError('Please select a unit');
      return false;
    }

    setMedicationError('');
    return true;
  };

  // Real-time validation to clear errors as user fixes inputs
  const checkValidationAndClearErrors = () => {
    // Only check if there's currently an error
    if (medicationError) {
      validateMedicationForm();
    }
  };

  // Handlers
  const handleAddMedication = () => {
    if (!medicationForm.medication) {
      setMedicationError('Please select a medication');
      return;
    }

    if (!validateMedicationForm()) {
      return;
    }

    const newMedication: Medication = {
      medication_type_id: medicationForm.medication_type_id,
      name: medicationForm.medication,
      dosage: medicationForm.dosage,           // String
      unit: medicationForm.unit,               // String
      amount: parseFloat(medicationForm.amount), // Number
      amount_per: medicationForm.amount_per,
      duration: parseInt(medicationForm.duration, 10), // Integer
      comments: medicationForm.comments,
      per_day: '',
      how_many_days: '',
      amount_per_take: 0,
      number_of_packages: 0,
      likelihood_of_med_in_predictix: '',
      start_date: null,
    };

    setMedications([...medications, newMedication]);
    setMedicationForm({
      medication: '',
      medication_type_id: 0,
      dosage: '',
      unit: '',
      amount: '',
      amount_per: 'Day',
      duration: '',
      comments: '',
    });
    setMedicationError('');
    onMedicationsChange?.();
  };

  const handleRemoveMedication = (index: number) => {
    setMedications(medications.filter((_, i) => i !== index));
    onMedicationsChange?.();
  };

  const getFilteredMedicationTypes = () => {
    if (!medicationSearch) return medicationTypes.slice(0, 50);
    const search = medicationSearch.toLowerCase();
    return medicationTypes
      .filter(m => m.name.toLowerCase().includes(search))
      .slice(0, 50);
  };

  const handleSelectMedicationType = (med: MedicationType) => {
    setMedicationForm(prev => ({
      ...prev,
      medication: med.name,
      medication_type_id: med.id,
      dosage: med.dosage_amount.toString(),
      unit: med.dosage_unit,
      amount: med.freq_amount.toString(),
      amount_per: (med.freq_period || 'Day') as 'Day' | 'Week' | 'Month',
      duration: med.duration_in_days.toString(),
    }));
    setMedicationSearch('');
    setShowMedicationDropdown(false);
  };

  return (
    <div className="p-4 bg-[#EBF5F5] rounded-lg">
      {showToggle && (
        <div className="flex items-center justify-start gap-2 mb-3">
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={medicationsEnabled}
              onChange={(e) => setMedicationsEnabled?.(e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-9 h-5 bg-gray-400 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-[#3a7380]"></div>
          </label>
          <label className="text-xs font-medium text-gray-700">
            Recommended medications
          </label>
        </div>
      )}

      {medicationsEnabled && (
        <>
          {/* Medication Form */}
          <div className="grid grid-cols-7 gap-2 mb-4">
            {/* Medication autocomplete */}
            <div className="relative">
              <input
                type="text"
                placeholder="Medication"
                value={medicationSearch || medicationForm.medication}
                onChange={(e) => {
                  setMedicationSearch(e.target.value);
                  setMedicationForm(prev => ({
                    ...prev,
                    medication: e.target.value,
                  }));
                }}
                onFocus={() => setShowMedicationDropdown(true)}
                className="w-full px-3 py-2 border border-[#B4D9D4] rounded-lg bg-[#B5D9D4] focus:outline-none focus:ring-2 focus:ring-[#366A75] focus:border-transparent"
              />

              {/* Medication Dropdown */}
              {showMedicationDropdown && medicationSearch && (
                <>
                  {/* Backdrop to close dropdown */}
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => {
                      setShowMedicationDropdown(false);
                      setMedicationSearch('');
                    }}
                  />

                  {/* Dropdown menu */}
                  <div className="absolute z-20 w-[500px] mt-1 bg-white border border-[#B4D9D4] rounded-lg shadow-lg max-h-64 overflow-y-auto">
                    {getFilteredMedicationTypes().length > 0 ? (
                      getFilteredMedicationTypes().map((medType) => (
                        <div
                          key={medType.id}
                          onClick={() => handleSelectMedicationType(medType)}
                          className="px-3 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-200 last:border-b-0"
                        >
                          <div className="text-sm font-medium text-gray-900">
                            {medType.name}
                          </div>
                          <div className="text-xs text-gray-600">
                            {medType.dosage_amount} {medType.dosage_unit} •{' '}
                            {medType.freq_amount}x/{medType.freq_period} •{' '}
                            {medType.duration_in_days} days
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="px-3 py-2 text-sm text-gray-500">
                        No medications found
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            <input
              type="number"
              placeholder="Dosage"
              step="0.01"
              value={medicationForm.dosage}
              onChange={(e) => {
                setMedicationForm(prev => ({
                  ...prev,
                  dosage: e.target.value,
                }));
                checkValidationAndClearErrors();
              }}
              className="px-3 py-2 border border-[#B4D9D4] rounded-lg bg-[#B5D9D4] focus:outline-none focus:ring-2 focus:ring-[#366A75] focus:border-transparent"
            />

            {/* Unit Dropdown */}
            <select
              value={medicationForm.unit}
              onChange={(e) => {
                setMedicationForm(prev => ({
                  ...prev,
                  unit: e.target.value,
                }));
                checkValidationAndClearErrors();
              }}
              className="px-3 py-2 border border-[#B4D9D4] rounded-lg bg-[#B5D9D4] focus:outline-none focus:ring-2 focus:ring-[#366A75] focus:border-transparent"
            >
              <option value="">Select unit</option>
              {MEDICATION_UNIT_OPTIONS.map(option => (
                <option key={option.id} value={option.label}>
                  {option.label}
                </option>
              ))}
            </select>

            <input
              type="number"
              placeholder="Amount"
              step="0.01"
              value={medicationForm.amount}
              onChange={(e) => {
                setMedicationForm(prev => ({
                  ...prev,
                  amount: e.target.value,
                }));
                checkValidationAndClearErrors();
              }}
              className="px-3 py-2 border border-[#B4D9D4] rounded-lg bg-[#B5D9D4] focus:outline-none focus:ring-2 focus:ring-[#366A75] focus:border-transparent"
            />

            <select
              value={medicationForm.amount_per}
              onChange={(e) =>
                setMedicationForm(prev => ({
                  ...prev,
                  amount_per: e.target.value as any,
                }))
              }
              className="px-3 py-2 border border-[#B4D9D4] rounded-lg bg-[#B5D9D4] focus:outline-none focus:ring-2 focus:ring-[#366A75] focus:border-transparent"
            >
              <option value="Day">Per day</option>
              <option value="Week">Per week</option>
              <option value="Month">Per month</option>
            </select>

            <input
              type="number"
              placeholder="Duration (Days)"
              step="1"
              pattern="[0-9]+"
              value={medicationForm.duration}
              onChange={(e) => {
                setMedicationForm(prev => ({
                  ...prev,
                  duration: e.target.value,
                }));
                checkValidationAndClearErrors();
              }}
              className="px-3 py-2 border border-[#B4D9D4] rounded-lg bg-[#B5D9D4] focus:outline-none focus:ring-2 focus:ring-[#366A75] focus:border-transparent"
            />

            <button
              onClick={handleAddMedication}
              className="w-8 h-8 flex items-center justify-center bg-white text-black rounded-lg shadow-sm hover:bg-[#D0E7E5] transition-all font-bold border-0 outline-none text-xl cursor-pointer"
            >
              +
            </button>
          </div>

          <input
            type="text"
            placeholder="Comments"
            value={medicationForm.comments}
            onChange={(e) =>
              setMedicationForm(prev => ({
                ...prev,
                comments: e.target.value,
              }))
            }
            className="w-full px-3 py-2 mb-2 border border-[#B4D9D4] rounded-lg bg-[#B5D9D4] focus:outline-none focus:ring-2 focus:ring-[#366A75] focus:border-transparent"
          />

          {medicationError && (
            <div className="text-red-600 text-sm mb-2">{medicationError}</div>
          )}

          {/* Medications Table */}
          {medications.length > 0 && (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium text-gray-700">
                      Name
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700">
                      Dosage
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700">
                      Unit
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700">
                      Amount
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700">
                      Amount per
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700">
                      Duration (Days)
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700">
                      Comments
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {medications.map((med, index) => {
                    const medType = medicationTypes.find(mt => mt.id === med.medication_type_id);
                    const medName = medType?.name || med.name || "-";
                    return (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-3 py-2">{medName}</td>
                      <td className="px-3 py-2">{med.dosage}</td>
                      <td className="px-3 py-2">{med.unit}</td>
                      <td className="px-3 py-2">{med.amount}</td>
                      <td className="px-3 py-2">{med.amount_per}</td>
                      <td className="px-3 py-2">{med.duration}</td>
                      <td className="px-3 py-2">{med.comments || '-'}</td>
                      <td className="px-3 py-2">
                        <button
                          onClick={() => handleRemoveMedication(index)}
                          className="text-red-600 hover:text-red-800"
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};
